import { useState } from "react";
import { docsClient, authClient, type DocumentInsert } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { generateDocumentHash } from "@/lib/hash";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, X, Sparkles, Link as LinkIcon } from "lucide-react";
import AIAddDocumentDialog from "./AIAddDocumentDialog";

interface AddDocumentDialogProps {
  onDocumentAdded: () => void;
}

export default function AddDocumentDialog({ onDocumentAdded }: AddDocumentDialogProps) {
  const [open, setOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const { toast } = useToast();

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !tags.includes(tag)) {
      if (tags.length >= 10) {
        toast({
          title: "Error",
          description: "Maximum 10 tags allowed",
          variant: "destructive",
        });
        return;
      }
      if (tag.length > 20) {
        toast({
          title: "Error",
          description: "Tags must be 20 characters or less",
          variant: "destructive",
        });
        return;
      }
      setTags([...tags, tag]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate URL
      try {
        new URL(url);
      } catch {
        toast({
          title: "Error",
          description: "Please enter a valid URL",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // Generate SHA-256 hash from URL
      const hash = await generateDocumentHash(url);

      // Get current user from authClient (Agency-Auth SSO)
      const {
        data: { user },
      } = await authClient.auth.getUser();

      if (!user) {
        toast({
          title: "Error",
          description: "You must be logged in to add documents",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      const documentPayload: DocumentInsert = {
        user_id: user.id,
        hash,
        url,
        title: title || null,
        description: description || null,
        content: null, // Will be filled by Edge Function later
        tags,
        size: null, // Will be calculated when content is downloaded
        processing_status: "pending",
      };

      // Insert document (Supabase expects an array payload)
      const { error } = await docsClient.from("documents").insert(documentPayload);

      if (error) {
        // Check for duplicate
        if (error.code === "23505") {
          toast({
            title: "Error",
            description: "This URL has already been added",
            variant: "destructive",
          });
        } else {
          throw error;
        }
        setLoading(false);
        return;
      }

      toast({
        title: "Success",
        description: "Document added successfully",
      });

      // Reset form
      setUrl("");
      setTitle("");
      setDescription("");
      setTags([]);
      setTagInput("");
      setManualOpen(false);

      // Notify parent to reload documents
      onDocumentAdded();
    } catch (error) {
      console.error("Error adding document:", error);
      toast({
        title: "Error",
        description: "Failed to add document",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Add Document
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Add Documentation</DialogTitle>
            <DialogDescription>
              Choose how you'd like to add documentation to your library
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-6">
            <Button
              variant="outline"
              className="h-auto p-4 justify-start items-start text-left w-full flex whitespace-normal"
              onClick={() => {
                setOpen(false);
                setAiDialogOpen(true);
              }}
            >
              <div className="flex gap-3 w-full min-w-0">
                <Sparkles className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1 space-y-1 min-w-0">
                  <div className="font-semibold">AI-Assisted Discovery (Recommended)</div>
                  <p className="text-sm text-muted-foreground break-words">
                    Chat with AI to find documentation. Just describe what you need: "Find React
                    docs" or "I need FastAPI deployment guides"
                  </p>
                </div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto p-4 justify-start items-start text-left w-full flex whitespace-normal"
              onClick={() => {
                setOpen(false);
                // Show manual form dialog
                setTimeout(() => setManualOpen(true), 100);
              }}
            >
              <div className="flex gap-3 w-full min-w-0">
                <LinkIcon className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 space-y-1 min-w-0">
                  <div className="font-semibold">Manual Entry</div>
                  <p className="text-sm text-muted-foreground break-words">
                    Add documentation by entering the URL directly if you already know it
                  </p>
                </div>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Dialog */}
      <AIAddDocumentDialog
        open={aiDialogOpen}
        onOpenChange={setAiDialogOpen}
        onDocumentAdded={onDocumentAdded}
      />

      {/* Manual Entry Dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Add Document Manually</DialogTitle>
              <DialogDescription>
                Add a new documentation URL to your collection. Content will be downloaded
                automatically.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="url">
                  URL <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="url"
                  placeholder="https://docs.example.com/guide"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="title">Title (optional)</Label>
                <Input
                  id="title"
                  placeholder="e.g., Installation Guide"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Textarea
                  id="description"
                  placeholder="Brief description of this documentation"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={loading}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tags">Tags (optional) - Press Enter to add</Label>
                <div className="flex gap-2">
                  <Input
                    id="tags"
                    placeholder="e.g., api, authentication"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={loading}
                  />
                  <Button type="button" variant="outline" onClick={handleAddTag} disabled={loading}>
                    Add
                  </Button>
                </div>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="gap-1">
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Max 10 tags, 20 characters each. Use alphanumeric and hyphens only.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setManualOpen(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Adding..." : "Add Document"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
